<script setup lang="ts">
/**
 * **框 / 注释的共用外壳** —— 显式宽高 + 右下角缩放。
 *
 * 两者只差「里面能不能写字」，所以合成一个组件：
 * 尺寸逻辑写两遍的话，改一处就会漏另一处。
 *
 * ## ⚠️ 框主体必须 `pointer-events: none`
 *
 * 这样在框覆盖的区域里**还能框选** —— 否则一个占满屏幕的框会把框选整个吃掉。
 * 只有标题栏和右下角把手单独打开事件。
 *
 * ⚠️ 还有一层：画布的 `.np-graph__node` **包装元素**同样覆盖整块区域，
 * 它默认是 `pointer-events: all`。所以宿主要给框节点的 `style` 也设上
 * `pointerEvents: 'none'`（见 Playground 的 makeFlowNode），
 * 只关内层 div 是不够的 —— 框里的节点会点不到。
 */
import { ref } from 'vue'
import { MIN_FRAME_HEIGHT, MIN_FRAME_WIDTH } from '../../types/node'
import type { GraphNodeModel } from '../../types/node'

const props = defineProps<{
  model: GraphNodeModel
  selected?: boolean
  active?: boolean
  /** 注释形态（可以写字）还是框形态 */
  note?: boolean
}>()

const emit = defineEmits<{
  (e: 'box-resize', width: number, height: number): void
  (e: 'note-update', text: string): void
}>()

const resizing = ref(false)
let start = { x: 0, y: 0, width: 0, height: 0 }
let scale = 1

function onResizeStart(ev: PointerEvent) {
  if (ev.button !== 0) return
  ev.preventDefault()

  const nodeEl = (ev.currentTarget as HTMLElement).parentElement
  if (!nodeEl) return

  // 跟改宽一样：用「实测 / 布局」反解画布缩放，本组件不需要知道图引擎状态
  const rect = nodeEl.getBoundingClientRect()
  scale = nodeEl.offsetWidth > 0 ? rect.width / nodeEl.offsetWidth : 1

  start = {
    x: ev.clientX,
    y: ev.clientY,
    // 基准用布局尺寸，rect 是屏幕像素 —— 混用会让尺寸跳变
    width: nodeEl.offsetWidth,
    height: nodeEl.offsetHeight,
  }
  resizing.value = true
  ;(ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId)
}

function onResizeMove(ev: PointerEvent) {
  if (!resizing.value) return
  const dx = (ev.clientX - start.x) / scale
  const dy = (ev.clientY - start.y) / scale
  emit(
    'box-resize',
    Math.max(MIN_FRAME_WIDTH, Math.round(start.width + dx)),
    Math.max(MIN_FRAME_HEIGHT, Math.round(start.height + dy)),
  )
}

function onResizeEnd(ev: PointerEvent) {
  if (!resizing.value) return
  resizing.value = false
  ;(ev.currentTarget as HTMLElement).releasePointerCapture?.(ev.pointerId)
}
</script>

<template>
  <div
    class="np-node"
    :class="props.note ? 'np-node--note' : 'np-node--frame'"
    :style="{
      width: `${props.model.width ?? 0}px`,
      height: `${props.model.height ?? 0}px`,
    }"
    :data-node-id="props.model.id"
    :data-node-type="props.model.type"
  >
    <header :class="props.note ? 'np-node__note-header' : 'np-node__frame-header'">
      <span :class="props.note ? 'np-node__note-title' : 'np-node__frame-title'">
        {{ props.model.label || props.model.title }}
      </span>
    </header>

    <textarea
      v-if="props.note"
      class="np-node__note-body nodrag nowheel"
      :value="props.model.note ?? ''"
      placeholder="写点什么…（注释不参与输出）"
      spellcheck="false"
      @input="emit('note-update', ($event.target as HTMLTextAreaElement).value)"
      @pointerdown.stop
    />

    <span
      class="np-node__box-resize nodrag"
      role="separator"
      title="拖动改尺寸"
      @pointerdown.stop="onResizeStart"
      @pointermove="onResizeMove"
      @pointerup="onResizeEnd"
      @pointercancel="onResizeEnd"
    />
  </div>
</template>
